<script setup>
import { computed } from 'vue';
import { Ref } from 'rete-vue-plugin';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  emit: {
    type: Function,
    required: true,
  },
  seed: {
    type: Number,
    default: 0,
  },
});

const inputs = computed(() => Object.entries(props.data.inputs || {}));
const outputs = computed(() => Object.entries(props.data.outputs || {}));
const isStartNode = computed(() => props.data.data?.type === 'start');
const isInteractiveNode = computed(
  () => props.data.data?.type === 'interactive'
);
const isTemplateNode = computed(() => props.data.data?.type === 'template');
const nodeType = computed(() => props.data.data?.type || '');
const nodeIcon = computed(() => {
  if (isStartNode.value) return 'i-lucide-person-standing';
  if (isInteractiveNode.value) return 'i-lucide-newspaper';
  if (isTemplateNode.value) return 'i-lucide-clipboard-plus';
  if (nodeType.value === 'button') return 'i-lucide-mouse-pointer-click';
  if (nodeType.value === 'list_message') return 'i-lucide-list';
  if (nodeType.value === 'section') return 'i-lucide-layout-grid';
  if (nodeType.value === 'row') return 'i-lucide-rows-3';
  if (nodeType.value === 'commerce') return 'i-lucide-shopping-bag';
  return 'i-lucide-message-square-text';
});
const nodeTitle = computed(() => {
  if (isStartNode.value) return 'Start Bot Flow';
  if (isInteractiveNode.value) {
    return props.data.data?.title || 'Interactive';
  }
  return props.data.data?.title || props.data.label;
});
const stats = computed(() => {
  if (!isInteractiveNode.value) return [];

  return [
    { label: 'Sent', value: 0, icon: 'i-lucide-send', color: 'text-sky-500' },
    {
      label: 'Delivered',
      value: 0,
      icon: 'i-lucide-badge-check',
      color: 'text-emerald-500',
    },
    {
      label: 'Subscribers',
      value: 0,
      icon: 'i-lucide-circle-user-round',
      color: 'text-blue-500',
    },
    {
      label: 'Errors',
      value: 0,
      icon: 'i-lucide-bug',
      color: 'text-rose-500',
    },
  ];
});
const templateMeta = computed(() => {
  if (!isTemplateNode.value) return '';

  const name = props.data.data?.templateName;
  const language = props.data.data?.templateLanguage;

  if (name && language) {
    return `${name} • ${language}`;
  }

  return name || '';
});
const nodeMeta = computed(() => {
  if (isStartNode.value) return '';
  if (isTemplateNode.value) return templateMeta.value;
  if (nodeType.value === 'text') {
    return props.data.data?.body?.toString?.().trim?.() || '';
  }
  if (nodeType.value === 'button') {
    if (props.data.data?.actionType === 'start_flow') {
      return (
        props.data.data?.targetFlowName || props.data.data?.buttonTitle || ''
      );
    }

    return (
      props.data.data?.body?.toString?.().trim?.() ||
      props.data.data?.buttonTitle ||
      ''
    );
  }
  if (nodeType.value === 'list_message') {
    return props.data.data?.buttonLabel || '';
  }
  if (nodeType.value === 'section') return props.data.data?.sectionTitle || '';
  if (nodeType.value === 'row') return props.data.data?.rowTitle || '';
  return '';
});
const startPreviewLines = computed(() => {
  if (!isStartNode.value) return [];

  const keyword = props.data.data?.keyword?.trim?.() || '';
  let matchType = 'No match';

  if (props.data.data?.triggerMode === 'keyword') {
    matchType =
      props.data.data?.matchType === 'contains'
        ? 'Contains keyword match'
        : 'Exact keyword match';
  }

  return [keyword, matchType].filter(Boolean);
});
const interactivePreviewLines = computed(() => {
  if (!isInteractiveNode.value) return [];

  const lines = [];
  const headerType = props.data.data?.headerType || 'none';
  const headerText = props.data.data?.headerText?.toString?.().trim?.() || '';
  const body = props.data.data?.body?.toString?.().trim?.() || '';
  const footer = props.data.data?.footer?.toString?.().trim?.() || '';

  if (headerType === 'text' && headerText) {
    lines.push(headerText);
  }

  if (body) {
    lines.push(body);
  }

  if (footer) {
    lines.push(footer);
  }

  return lines;
});
</script>

<template>
  <div
    class="flow-node"
    :class="{
      'flow-node--selected': data.selected,
      'flow-node--start': isStartNode,
      'flow-node--interactive': isInteractiveNode,
    }"
    :data-node-id="String(data.id)"
    :data-node-type="data.data?.type || ''"
    data-testid="node"
  >
    <header class="flow-node__header">
      <div class="flow-node__title-group">
        <span class="flow-node__icon" :class="nodeIcon" />
        <span class="flow-node__title">{{ nodeTitle }}</span>
      </div>
    </header>

    <div v-if="isInteractiveNode" class="flow-node__stats">
      <div v-for="item in stats" :key="item.label" class="flow-node__stat">
        <span class="flow-node__stat-label">
          <span :class="[item.icon, item.color]" />
          {{ item.label }}
        </span>
        <span class="flow-node__stat-value">{{ item.value }}</span>
      </div>
    </div>

    <div class="flow-node__body">
      <span class="i-lucide-hand flow-node__body-icon" />
      <template v-if="isStartNode">
        <span
          v-for="(line, index) in startPreviewLines"
          :key="`${data.id}-start-preview-${index}`"
          class="flow-node__meta"
        >
          {{ line }}
        </span>
      </template>
      <template v-else-if="isInteractiveNode">
        <span
          v-for="(line, index) in interactivePreviewLines"
          :key="`${data.id}-interactive-preview-${index}`"
          class="flow-node__meta"
        >
          {{ line }}
        </span>
      </template>
      <span v-if="nodeMeta" class="flow-node__meta">
        {{ nodeMeta }}
      </span>
    </div>

    <footer class="flow-node__footer">
      <div class="flow-node__inputs">
        <div
          v-for="[key, input] in inputs"
          :key="`input-${key}-${seed}`"
          class="flow-node__port flow-node__port--input"
        >
          <Ref
            class="flow-node__socket flow-node__socket--input"
            :emit="emit"
            :data="{
              type: 'socket',
              side: 'input',
              key,
              nodeId: data.id,
              payload: input.socket,
            }"
            data-flow-socket="true"
            :data-node-id="String(data.id)"
            :data-socket-key="key"
            data-socket-side="input"
          />
          <span class="flow-node__port-label">{{ input.label }}</span>
        </div>
      </div>

      <div class="flow-node__outputs">
        <div
          v-for="[key, output] in outputs"
          :key="`output-${key}-${seed}`"
          class="flow-node__port flow-node__port--output"
        >
          <span class="flow-node__port-label">{{ output.label }}</span>
          <Ref
            class="flow-node__socket flow-node__socket--output"
            :emit="emit"
            :data="{
              type: 'socket',
              side: 'output',
              key,
              nodeId: data.id,
              payload: output.socket,
            }"
            data-flow-socket="true"
            :data-node-id="String(data.id)"
            :data-socket-key="key"
            data-socket-side="output"
          />
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
.flow-node {
  width: 392px;
  min-height: 236px;
  border: 2px solid #d7e5f7;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.06);
  color: #0f172a;
  user-select: none;
  direction: ltr;
}

.flow-node--selected {
  border-color: #1d8cff;
  box-shadow: 0 0 0 2px rgba(29, 140, 255, 0.1);
}

.flow-node__header {
  padding: 1.15rem 1.35rem 0.75rem;
}

.flow-node__title-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.flow-node__icon {
  font-size: 1.2rem;
  color: #0a4d88;
}

.flow-node__title {
  font-size: 1rem;
  font-weight: 700;
  color: #0a4d88;
}

.flow-node__stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.25rem;
  padding: 0 1.35rem 0.4rem;
}

.flow-node__stat {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  align-items: center;
}

.flow-node__stat-label {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  color: #6b7280;
  font-size: 0.76rem;
}

.flow-node__stat-value {
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
}

.flow-node__body {
  display: grid;
  place-items: center;
  min-height: 94px;
  padding: 0.25rem 0 0.75rem;
  gap: 0.5rem;
}

.flow-node__body-icon {
  font-size: 2.4rem;
  color: #374151;
}

.flow-node__meta {
  max-width: 260px;
  overflow: hidden;
  color: #64748b;
  font-size: 0.82rem;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flow-node__footer {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  border-top: 1px dashed #b8cce2;
  padding: 1rem 1.2rem 1.1rem;
}

.flow-node__inputs,
.flow-node__outputs {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.flow-node__outputs {
  align-items: flex-end;
  margin-left: auto;
}

.flow-node__port {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.flow-node__port--output {
  justify-content: flex-end;
}

.flow-node__port-label {
  font-size: 0.94rem;
  color: #0a4d88;
  white-space: nowrap;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.flow-node__socket:deep(.socket) {
  width: 22px;
  height: 22px;
  margin: 0;
  border: 3px solid #8a9aae;
  background: #fff;
  box-shadow: none;
}

.flow-node__socket {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  cursor: crosshair;
  position: relative;
  z-index: 3;
}

.flow-node__socket--output:deep(.socket) {
  margin-right: -32px;
}

.flow-node__socket--input:deep(.socket) {
  margin-left: -32px;
}
</style>
